<template>
  <div class="fppw" role="dialog" aria-modal="true" :aria-labelledby="titleId">
    <div class="fppw-header">
      <div class="fppw-featured-icon" aria-hidden="true">
        <img
          class="fppw-featured-icon__img"
          src="./assets/currency-banknote.svg"
          alt=""
          width="22"
          height="18"
        />
      </div>
      <button type="button" class="fppw-close" aria-label="Close" @click="$emit('close')">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M18 6 6 18M6 6l12 12"
            stroke="#667085"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>

    <div class="fppw-main">
      <div class="fppw-title-row">
        <h2 :id="titleId" class="fppw-title">Create First Base Pay Plan</h2>
        <span v-if="step > 1" class="fppw-effective">{{ effectiveHeader }}</span>
        <span class="fppw-step-label">Step {{ step }} of {{ totalSteps }}</span>
      </div>

      <!-- Step 1: Effective from / to, info banner, scenario panel -->
      <div v-if="step === 1" class="fppw-body">
        <EffectiveDateToEffectiveTo
          :effective-from="effectiveDate"
          :effective-to="effectiveBy"
          :disabled-dates="disabledEffectiveFrom"
          :effective-to-disabled-dates="disabledEffectiveTo"
          :effective-to-enabled="effectiveToEnabled"
          @update:effectiveFrom="onEffectiveFromChange"
          @update:effectiveTo="onEffectiveToChange"
        />

        <div class="fppw-alert">
          <div class="fppw-alert__icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="7.5" stroke="#667085" stroke-width="1.5" />
              <path
                d="M10 9v4M10 6.5h.01"
                stroke="#667085"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div class="fppw-alert__content">
            <p class="fppw-alert__title">You are about to create directly a pay plan</p>
            <p class="fppw-alert__body">
              Directly creating a plan means that the employee already has an in force
              employment agreement in payroll and you want to create it here as a Pay Plan
            </p>
          </div>
        </div>

        <FairPayCalculatorAtEffectiveDateCard
          v-if="step1CalculatorMode"
          class="fppw-calculator"
          :calculator="step1CalculatorMode"
          :as-of-date="formattedEffectiveDate"
          :position="calculatorPosition"
          :pay-range-version="calculatorPayRangeVersion"
          :pay-range-adjusted="calculatorPayRangeAdjusted"
          :fte-adjustment-applied="fteAdjustmentApplied"
          @learn-more="$emit('learn-more')"
        />
        <template v-else>
          <div v-if="hasPosition" class="fppw-chip">
            <span class="fppw-chip__label">Employee primary position at effective date</span>
            <span class="fppw-chip__value">
              <span class="fppw-chip__code">{{ payRange.positionCode }}</span>
              <span v-if="payRange.positionName">{{ payRange.positionName }}</span>
            </span>
          </div>
          <div class="fppw-alert fppw-alert--warn" role="status">
            <div class="fppw-alert__icon fppw-alert__icon--warn" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="7.5" stroke="#B54708" stroke-width="1.5" />
                <path
                  d="M10 6.5v4M10 13h.01"
                  stroke="#B54708"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div class="fppw-alert__content">
              <p class="fppw-alert__title">{{ scenarioAlertTitle }}</p>
              <p class="fppw-alert__body">We cannot suggest a Fair base salary proposal for it</p>
            </div>
          </div>
        </template>
      </div>

      <!-- Step 2: terms, fairness check, calculator -->
      <div v-else class="fppw-body">
        <div v-if="!hasPayRange" class="fppw-alert fppw-alert--warn" role="status">
          <div class="fppw-alert__icon fppw-alert__icon--warn" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="7.5" stroke="#B54708" stroke-width="1.5" />
              <path
                d="M10 6.5v4M10 13h.01"
                stroke="#B54708"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div class="fppw-alert__content">
            <p class="fppw-alert__title">{{ scenarioAlertTitle }}</p>
            <p class="fppw-alert__body">
              We cannot suggest a Fair base salary proposal for it. Country and Currency
              default to the account operating country and reference currency.
            </p>
          </div>
        </div>

        <div v-if="hasPosition && !hasPayRange" class="fppw-chip">
          <span class="fppw-chip__label">Employee primary position at effective date</span>
          <span class="fppw-chip__value">
            <span class="fppw-chip__code">{{ payRange.positionCode }}</span>
            <span v-if="payRange.positionName">{{ payRange.positionName }}</span>
          </span>
        </div>

        <div class="fppw-grid">
          <div
            class="fppw-field fppw-field--picker"
            :class="{ 'fppw-select--locked': countryCurrencyLocked }"
          >
            <CountryPicker
              class="fppw-picker"
              :class="{ 'fppw-select--locked': countryCurrencyLocked }"
              pickerLabel="Country"
              label="Type to search"
              searchLabel="Search country"
              :listSource="countryListSource"
              :useStaticFallback="countryListSource === 'static'"
              :items="countryItems"
              :value="country"
              :isforceReadonly="countryCurrencyLocked"
              :isSelectedItemRemove="!countryCurrencyLocked"
              @item-country-selected="onCountrySelected"
              @clear-selection="onCountryCleared"
              @input="onCountrySelected"
            />
          </div>

          <div
            class="fppw-field fppw-field--picker"
            :class="{ 'fppw-select--locked': countryCurrencyLocked }"
          >
            <CurrencyPicker
              class="fppw-picker"
              :class="{ 'fppw-select--locked': countryCurrencyLocked }"
              pickerLabel="Currency"
              label="Currency"
              searchLabel="Select Currency"
              :listSource="currencyListSource"
              :items="currencyItems"
              :value="currency"
              :isforceReadonly="countryCurrencyLocked"
              :isSelectedItemRemove="!countryCurrencyLocked"
              @item-currency-selected="onCurrencySelected"
              @clear-selection="onCurrencyCleared"
              @input="onCurrencySelected"
            />
          </div>

          <div class="fppw-field">
            <label class="fppw-label" :for="salaryInputId">Base Salary*</label>
            <div class="fppw-salary" :class="{ 'fppw-salary--invalid': !baseSalaryValid }">
              <input
                :id="salaryInputId"
                class="fppw-salary__input"
                type="text"
                inputmode="decimal"
                :value="formattedBaseSalary"
                :aria-invalid="String(!baseSalaryValid)"
                @input="onSalaryInput"
              />
              <span class="fppw-salary__suffix">{{ salarySuffix }}</span>
            </div>
          </div>

          <div
            class="fppw-field fppw-field--picker fppw-field--pay-basis"
            :class="{ 'fppw-select--locked': countryCurrencyLocked }"
          >
            <ExSelectPicker
              class="fppw-picker"
              :class="{ 'fppw-select--locked': countryCurrencyLocked }"
              pickerLabel="Pay Basis"
              label="Pay Basis"
              searchLabel="Select Pay Basis"
              :items="payBasisOptions"
              :value="selectedPayBasis"
              :hasImageItem="false"
              :isforceReadonly="countryCurrencyLocked"
              :isSelectedItemRemove="!countryCurrencyLocked"
              @item-selected="onPayBasisSelected"
              @clear-selection="onPayBasisCleared"
              @input="onPayBasisSelected"
            />
          </div>
        </div>

        <div v-if="fairnessMessage" class="fppw-fairness" :class="fairnessClass" role="status">
          <CheckCircleIcon v-if="fairnessIcon === 'check-circle'" class="fppw-fairness__icon" />
          <AlertTriangleIcon
            v-else-if="fairnessIcon === 'alert-triangle'"
            class="fppw-fairness__icon"
          />
          <span class="fppw-fairness__text">{{ fairnessMessage }}</span>
        </div>

        <PayRangePenetrationBar
          v-if="penetrationMode"
          class="fppw-calculator"
          :range-penetration="payRange.rangePenetration"
          :currency-symbol="resolvedCurrencySymbol"
        />
        <FairPayCalculatorAtEffectiveDateCard
          v-else-if="hasPayRange"
          class="fppw-calculator"
          :calculator="step2CalculatorMode"
          :as-of-date="formattedEffectiveDate"
          :position="calculatorPosition"
          :pay-range-version="calculatorPayRangeVersion"
          :pay-range-adjusted="calculatorPayRangeAdjusted"
          :fte-adjustment-applied="fteAdjustmentApplied"
          :current-readiness="currentReadiness"
          :fair-at-current="fairAtCurrent"
          :simulated-readiness="simulatedReadiness"
          :fair-at-simulated="fairAtSimulated"
          @update:simulatedReadiness="onSimulatedReadiness"
          @reset-simulation="onResetSimulation"
          @learn-more="$emit('learn-more')"
        />
      </div>
    </div>

    <div class="fppw-actions">
      <button
        v-if="step > 1"
        type="button"
        class="fppw-btn fppw-btn--secondary"
        @click="$emit('back')"
      >
        Back
      </button>
      <template v-if="step === 1">
        <button
          type="button"
          class="fppw-btn fppw-btn--primary"
          :disabled="!canContinue"
          @click="$emit('next')"
        >
          Next
        </button>
      </template>
      <template v-else>
        <button
          type="button"
          class="fppw-btn fppw-btn--primary"
          :disabled="!canSubmit"
          @click="$emit('create-pay-plan')"
        >
          Create Base Pay Plan
        </button>
      </template>
    </div>
  </div>
</template>

<script>
export const CALCULATOR_MODE = {
  RANGE: "range",
  NO_RANGE: "no-range",
  NO_POSITION: "no-position",
};

export const PAY_BASIS = {
  HOURLY: "hourly",
  MONTHLY: "monthly",
  ANNUAL: "annual",
};

export const PAY_BASIS_OPTIONS = [
  { id: PAY_BASIS.HOURLY, name: "Hourly", code: "HOURLY" },
  { id: PAY_BASIS.MONTHLY, name: "Monthly", code: "MONTHLY" },
  { id: PAY_BASIS.ANNUAL, name: "Annually", code: "ANNUAL" },
];
</script>

<script setup>
import { computed, getCurrentInstance } from "vue";
import dayjs from "dayjs";
import CountryPicker from "@components/CountryPicker/CountryPicker.vue";
import CurrencyPicker from "@components/CurrencyPicker/CurrencyPicker.vue";
import ExSelectPicker from "@components/SelectPicker/ExSelectPicker.vue";
import EffectiveDateToEffectiveTo from "@components/EffectiveDateToEffectiveTo/EffectiveDateToEffectiveTo.vue";
import FairPayCalculatorAtEffectiveDateCard from "@components/FairPayCalculatorAtEffectiveDateCard/FairPayCalculatorAtEffectiveDateCard.vue";
import PayRangePenetrationBar from "@components/PayRangePenetrationBar/PayRangePenetrationBar.vue";
import CheckCircleIcon from "@components/icons/items/Generic/CheckCircleIcon.vue";
import AlertTriangleIcon from "@components/icons/items/Generic/AlertTriangleIcon.vue";
import {
  computeEquivalentReadiness,
  fairnessBannerClass,
  fairnessBannerIcon,
  formatFairnessMessage,
  resolveFairnessState,
} from "./fairnessCheck.js";
import {
  isCoveredByRanges,
  isEffectiveToDisabled,
  readinessForSalary,
  salaryForReadiness,
  snapReadiness,
  toISODate,
} from "./payPlanWizardMath.js";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

const props = defineProps({
  step: { type: Number, default: 1 },
  effectiveDate: {
    type: String,
    default: () => dayjs().format("YYYY-MM-DD"),
  },
  /** Optional Effective to (YYYY-MM-DD); only offered when `effectiveToEnabled` (idea-MDP-429). */
  effectiveBy: { type: String, default: null },
  /** Selected country `{ id, name, icon, code }` — use with CountryPicker. */
  country: { type: Object, default: null },
  /** Selected currency `{ id, name, icon, code }` — use with CurrencyPicker. */
  currency: { type: Object, default: null },
  /** Optional static country list (flags via `icon`). Empty → CountryPicker listSource. */
  countryItems: { type: Array, default: () => [] },
  /** Optional static currency list (flags via `icon`). Empty → CurrencyPicker listSource. */
  currencyItems: { type: Array, default: () => [] },
  /** `operating` (API) or `static` (Storybook, with countryItems). */
  countryListSource: {
    type: String,
    default: "operating",
    validator: (v) => ["static", "operating"].includes(v),
  },
  /** `operating` or `catalog`. Pass currencyItems in Storybook. */
  currencyListSource: {
    type: String,
    default: "operating",
    validator: (v) => ["catalog", "operating"].includes(v),
  },
  /** Fallback when the currency object has no code. */
  currencyCode: { type: String, default: "USD" },
  currencySymbol: { type: String, default: "$" },
  baseSalary: { type: [Number, String], default: null },
  payBasis: { type: String, default: "annual" },
  payBasisLabel: { type: String, default: "Annually" },
  /** range | no-range | no-position */
  calculatorMode: {
    type: String,
    default: CALCULATOR_MODE.NO_POSITION,
    validator: (v) => Object.values(CALCULATOR_MODE).includes(v),
  },
  payRange: { type: Object, default: () => ({}) },
  /** Readiness module flag from the pay-plan context (ER-11961). */
  readinessModuleEnabled: { type: Boolean, default: true },
  /** Days covered by the employee's other pay plans: `[{ from, to|null }]` (Rule 2a). */
  disabledRanges: { type: Array, default: () => [] },
  /** Start of the next plan; Effective to is disabled on/after it (Rule 2a). */
  nextConflictDate: { type: String, default: null },
  /** idea-MDP-429: offers Effective to. */
  effectiveToEnabled: { type: Boolean, default: false },
  /** When true, primary CTAs are busy (parent owns API). */
  submitting: { type: Boolean, default: false },
});

const emit = defineEmits([
  "close",
  "back",
  "next",
  "create-pay-plan",
  "learn-more",
  "update:effectiveDate",
  "update:effectiveBy",
  "update:baseSalary",
  "update:country",
  "update:currency",
  "update:payBasis",
  "update:payBasisLabel",
]);

const instance = getCurrentInstance();
const uid = instance?.uid ?? 0;
const titleId = `fppw-title-${uid}`;
const salaryInputId = `fppw-salary-${uid}`;

const totalSteps = 2;
const payBasisOptions = PAY_BASIS_OPTIONS;
const PAY_BASIS_SUFFIX = { hourly: "Hour", monthly: "Month", annual: "Year" };
const PAY_BASIS_SHORT = { hourly: "hr", monthly: "mo", annual: "yr" };

const selectedPayBasis = computed(
  () => PAY_BASIS_OPTIONS.find((o) => o.id === props.payBasis) || null
);

const formattedEffectiveDate = computed(() => formatFairPayDate(props.effectiveDate));

const effectiveHeader = computed(() =>
  props.effectiveBy
    ? `Effective from ${formattedEffectiveDate.value} to ${formatFairPayDate(props.effectiveBy)}`
    : `Effective from ${formattedEffectiveDate.value}`
);

const hasPayRange = computed(() => props.calculatorMode === CALCULATOR_MODE.RANGE);
const hasPosition = computed(() => Boolean(props.payRange?.positionCode));

/**
 * Rule 3a: when a pay range is in force at the effective date, Country, Used
 * Currency and Pay Basis are inherited from it and locked read-only. Changing
 * the effective date re-resolves the context and can unlock them.
 */
const countryCurrencyLocked = computed(() => hasPayRange.value);

const scenarioAlertTitle = computed(() =>
  props.calculatorMode === CALCULATOR_MODE.NO_POSITION
    ? "This employee does not occupy a primary position at effective date"
    : "Position does not have a Pay Range Subscription at effective date"
);

/** Step 1 panel: not-available (module off) → read-only (range) → static alert. */
const step1CalculatorMode = computed(() => {
  if (!props.readinessModuleEnabled) return "not-available";
  return hasPayRange.value ? "read-only" : null;
});

/** Rule A-40: Readiness off replaces the Fair Pay calculator with the range penetration bar. */
const penetrationMode = computed(() => !props.readinessModuleEnabled && hasPayRange.value);

const step2CalculatorMode = computed(() =>
  props.readinessModuleEnabled ? "current-and-simulated" : "simulated-only"
);

const disabledEffectiveFrom = (date) => isCoveredByRanges(date, props.disabledRanges);
const disabledEffectiveTo = (date) =>
  isEffectiveToDisabled(date, props.effectiveDate, props.nextConflictDate);

const resolvedCurrencyCode = computed(() => props.currency?.code || props.currencyCode);
const resolvedCurrencySymbol = computed(() => props.currency?.symbol || props.currencySymbol);

const baseSalaryNumber = computed(() => {
  if (props.baseSalary == null || props.baseSalary === "") return null;
  const n = Number(props.baseSalary);
  return Number.isFinite(n) ? n : null;
});

const baseSalaryValid = computed(
  () => baseSalaryNumber.value != null && baseSalaryNumber.value > 0
);

const formattedBaseSalary = computed(() => {
  if (baseSalaryNumber.value == null) {
    return props.baseSalary == null ? "" : String(props.baseSalary);
  }
  return baseSalaryNumber.value.toLocaleString("en-US", { maximumFractionDigits: 2 });
});

const salarySuffix = computed(
  () => `${resolvedCurrencyCode.value}/${PAY_BASIS_SUFFIX[props.payBasis] || "Year"}`
);

const tolerancePct = computed(() => {
  const value = Number(props.payRange?.tolerancePct);
  return Number.isFinite(value) ? value : 2;
});

// ── Fairness check (Rule 12): range found and Readiness module on only ──
const equivalentReadiness = computed(() => {
  if (!hasPayRange.value) return null;
  return computeEquivalentReadiness(
    baseSalaryNumber.value,
    props.payRange.rangeMin,
    props.payRange.rangeMax
  );
});

const fairnessState = computed(() => {
  if (!hasPayRange.value || baseSalaryNumber.value == null) return null;
  return resolveFairnessState({
    proposedAmount: baseSalaryNumber.value,
    fairPayAmount: props.payRange.fairPayAmount,
    rangeMin: props.payRange.rangeMin,
    rangeMax: props.payRange.rangeMax,
    currentReadinessPct: props.payRange.currentReadinessPct,
    tolerancePct: tolerancePct.value,
  });
});

const fairnessMessage = computed(() => {
  if (!fairnessState.value || !baseSalaryValid.value || !props.readinessModuleEnabled) return "";
  return formatFairnessMessage({
    state: fairnessState.value,
    proposedAmount: baseSalaryNumber.value,
    fairPayAmount: props.payRange.fairPayAmount,
    rangeMin: props.payRange.rangeMin,
    rangeMax: props.payRange.rangeMax,
    equivalentReadiness: equivalentReadiness.value,
    currentReadinessPct: props.payRange.currentReadinessPct,
    currencySymbol: resolvedCurrencySymbol.value,
  });
});

const fairnessClass = computed(() => fairnessBannerClass(fairnessState.value));
const fairnessIcon = computed(() => fairnessBannerIcon(fairnessState.value));

// ── Calculator card view-models ──
const calculatorPosition = computed(() => ({
  code: props.payRange?.positionCode || "",
  name: props.payRange?.positionName || "",
  fte: props.payRange?.allocationFte ?? 1,
}));

function anchorsOf(source) {
  return {
    0: source?.anchor0 ?? null,
    50: source?.anchor50 ?? null,
    100: source?.anchor100 ?? null,
    150: source?.anchor150 ?? null,
  };
}

const calculatorPayRangeVersion = computed(() => ({
  code: props.payRange?.payRangeCode || "",
  version: props.payRange?.versionNumber ?? "",
  asOfDate: formatFairPayDate(props.payRange?.versionAsOf),
  countryFlag: props.country?.icon || "",
  location: props.country?.name || "",
  payBasis: props.payRange?.payBasisCode || props.payBasis.toUpperCase(),
  currency: resolvedCurrencyCode.value,
  jobCategory: props.payRange?.positionName || "",
  level: "",
  anchors: anchorsOf(props.payRange),
}));

const fteAdjustmentApplied = computed(
  () => Boolean(props.payRange?.fteAdjustmentApplied) && Boolean(props.payRange?.adjusted)
);

const calculatorPayRangeAdjusted = computed(() =>
  fteAdjustmentApplied.value ? { anchors: anchorsOf(props.payRange.adjusted) } : null
);

const currentReadiness = computed(() => props.payRange?.currentReadinessPct ?? null);

function fairPayLabel(amount) {
  return {
    amount,
    currency: resolvedCurrencyCode.value,
    basis: PAY_BASIS_SHORT[props.payBasis] || "",
    variance: `±${tolerancePct.value}%`,
  };
}

const fairAtCurrent = computed(() => {
  const amount = props.payRange?.fairPayAmount;
  return amount == null ? null : fairPayLabel(amount);
});

// ── Slider ↔ Base Salary (Rule 3c): the slider is derived from the salary ──
const simulatedReadiness = computed(() => {
  const exact = readinessForSalary(
    props.payRange?.rangeMin,
    props.payRange?.rangeMax,
    props.baseSalary
  );
  return exact == null ? 0 : snapReadiness(exact);
});

const fairAtSimulated = computed(() => {
  const amount = salaryForReadiness(
    props.payRange?.rangeMin,
    props.payRange?.rangeMax,
    simulatedReadiness.value
  );
  return amount == null ? null : fairPayLabel(amount);
});

function onSimulatedReadiness(value) {
  const amount = salaryForReadiness(props.payRange?.rangeMin, props.payRange?.rangeMax, value);
  if (amount != null) emit("update:baseSalary", amount);
}

function onResetSimulation() {
  const amount = props.readinessModuleEnabled ? props.payRange?.fairPayAmount : null;
  emit("update:baseSalary", amount ?? null);
}

// ── Footer state ──
const canContinue = computed(() => !props.submitting && Boolean(props.effectiveDate));

const canSubmit = computed(
  () =>
    !props.submitting &&
    Boolean(props.country?.id || props.country?.name) &&
    Boolean(props.currency?.id || props.currency?.code) &&
    Boolean(props.payBasis) &&
    baseSalaryValid.value
);

function normalizeNumber(raw) {
  const cleaned = String(raw || "").replace(/,/g, "").trim();
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function onSalaryInput(event) {
  emit("update:baseSalary", normalizeNumber(event.target.value));
}

function onCountrySelected(item) {
  if (countryCurrencyLocked.value) return;
  emit("update:country", item || null);
  // Edge case 1: selecting a Country cascades its Currency; still changeable afterwards.
  if (item?.currency) {
    emit("update:currency", { ...item.currency, currency_id: item.currency.id });
  }
}

function onCountryCleared() {
  if (countryCurrencyLocked.value) return;
  emit("update:country", null);
}

function onCurrencySelected(item) {
  if (countryCurrencyLocked.value) return;
  emit("update:currency", item || null);
}

function onCurrencyCleared() {
  if (countryCurrencyLocked.value) return;
  emit("update:currency", null);
}

function onPayBasisSelected(item) {
  if (countryCurrencyLocked.value || !item?.id) return;
  emit("update:payBasis", item.id);
  emit("update:payBasisLabel", item.name);
}

function onPayBasisCleared() {
  if (countryCurrencyLocked.value) return;
  emit("update:payBasis", "");
  emit("update:payBasisLabel", "");
}

function onEffectiveFromChange(date) {
  const iso = toISODate(date);
  if (iso) emit("update:effectiveDate", iso);
}

function onEffectiveToChange(date) {
  emit("update:effectiveBy", date ? toISODate(date) : null);
}
</script>

<style scoped>
.fppw {
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 900px;
  max-width: 100%;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 24px -4px rgba(16, 24, 40, 0.08), 0 8px 8px -4px rgba(16, 24, 40, 0.03);
  overflow: visible;
  font-family: Inter, sans-serif;
  color: #101828;
}

.fppw::before {
  content: "";
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;
  width: 216px;
  height: 216px;
  background-image: url("@components/assets/Background_pattern_decorative_new.svg");
  background-repeat: no-repeat;
  background-position: top left;
  background-size: 216px 216px;
  opacity: 0.5;
  pointer-events: none;
}

.fppw > * {
  position: relative;
  z-index: 1;
}

/* The steps (and their floating picker dropdowns) live inside `.fppw-main`. Keep
 * that wrapper above the footer so an open dropdown isn't painted under (or
 * click-blocked by) the Back / Next actions. */
.fppw-main {
  z-index: 2;
}

.fppw-header {
  position: relative;
  padding: 24px 24px 20px;
}

.fppw-featured-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.fppw-featured-icon__img {
  display: block;
  width: 22px;
  height: 18px;
}

.fppw-close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.fppw-main {
  display: flex;
  flex-direction: column;
  gap: 17px;
  width: 100%;
}

.fppw-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 28px;
  padding: 0 24px;
}

.fppw-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 20px;
  color: #101828;
}

.fppw-effective {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.fppw-step-label {
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.fppw-body {
  display: flex;
  flex-direction: column;
  gap: 17px;
  padding: 0;
  padding-inline: 24px;
}

.fppw-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.fppw-alert {
  display: flex;
  gap: 16px;
  align-items: start;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.fppw-alert__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.fppw-alert__icon--warn {
  width: 20px;
  height: 20px;
  border: none;
  box-shadow: none;
}

.fppw-alert__content {
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.fppw-alert__title {
  margin: 0;
  padding-right: 32px;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.fppw-alert__body {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.fppw-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 17px 17px;
}

.fppw-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.fppw-field--picker {
  gap: 0;
}

.fppw-field--picker ::v-deep(.select-picker) {
  padding: 0 !important;
  margin: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
}

.fppw-field--picker ::v-deep(.container) {
  padding: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
}

/* Stretch the 352px picker; drop its 8px top margin so it lines up with salary. */
.fppw-field--picker ::v-deep(.custom-response-picker) {
  width: 100% !important;
  max-width: 100% !important;
  margin-top: 0 !important;
}

.fppw-field--picker ::v-deep(.list-picker-container),
.fppw-field--picker ::v-deep(.res-custom-list-picker) {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
}

.fppw-field--picker ::v-deep(.res-v-list-item) {
  min-width: 0 !important;
}

.fppw-field--picker ::v-deep(.label-name) {
  margin: 0 0 6px !important;
  padding: 0 !important;
  color: #344054 !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 20px !important;
}

.fppw-field--picker ::v-deep(.select-picker .v-input) {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
}

/* Rule 3a: only the input fill goes grey when inherited/locked — labels stay #344054. */
.fppw-select--locked {
  background: transparent;
  cursor: default;
}

.fppw-salary {
  display: flex;
  align-items: center;
  width: 100%;
  height: 50px;
  min-height: 50px;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  overflow: hidden;
}

.fppw-salary--invalid {
  background: #fee4e2;
}

.fppw-salary__input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  color: #475467;
}

.fppw-salary__input:focus {
  outline: none;
}

.fppw-salary__suffix {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  font-size: 16px;
  line-height: 24px;
  color: #344054;
  white-space: nowrap;
}

.fppw-fairness {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 99px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.fppw-fairness__text {
  min-width: 0;
}

.fppw-fairness__icon {
  display: block;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  overflow: visible;
}

.fppw-fairness--above {
  background: #fffaeb;
  border: 1px solid #FEDF89;
  color: #B54708;
}

.fppw-fairness--below {
  background: #FEF3F2;
  border: 1px solid #fecdca;
  color: #B42318;
}

.fppw-fairness--exact {
  background: #ECFDF3;
  border: 1px solid #ABEFC6;
  color: #067647;
}

.fppw-fairness--out-of-range {
  background: #fef3f2;
  border: 1px solid #fecdca;
  color: #b42318;
}

.fppw-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 32px 24px 24px;
}

.fppw-btn {
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.fppw-btn--secondary {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
}

.fppw-btn--primary {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
}

.fppw-btn--disabled,
.fppw-btn:disabled {
  background: #e0e0e0;
  border-color: #e0e0e0;
  color: #ffffff;
  cursor: not-allowed;
}

.fppw-calculator {
  width: 100%;
  box-sizing: border-box;
}

.fppw-chip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 10px 14px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}

.fppw-chip__label {
  font-weight: 500;
  color: #475467;
}

.fppw-chip__value {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #344054;
}

.fppw-chip__code {
  padding: 2px 8px;
  background: #f9f5ff;
  border: 1px solid #e9d7fe;
  border-radius: 16px;
  font-weight: 600;
  color: #6941c6;
}

.fppw-alert--warn {
  background: #fffaeb;
  border-color: #fedf89;
}

.fppw-alert--warn .fppw-alert__title {
  color: #93370d;
}

.fppw-alert--warn .fppw-alert__body {
  color: #b54708;
}

</style>
